import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateMedicalTemplateInput,
  MedicalTemplate,
  UpdateMedicalTemplateInput,
} from "@/features/medicalTemplates/schemas/medicalTemplate";
import {
  createMedicalTemplate,
  deleteMedicalTemplate,
  listMedicalTemplates,
  updateMedicalTemplate,
} from "@/features/medicalTemplates/services/medicalTemplatesService";

export const medicalTemplateKeys = {
  all: ["medicalTemplates"] as const,
  list: () => [...medicalTemplateKeys.all, "list"] as const,
};

/**
 * Plantillas de historia médica desde Firestore (`medical_templates`).
 */
export function useMedicalTemplates() {
  return useQuery<MedicalTemplate[]>({
    queryKey: medicalTemplateKeys.list(),
    queryFn: listMedicalTemplates,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateMedicalTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      input,
      authorUid,
    }: {
      input: CreateMedicalTemplateInput;
      authorUid: string;
    }) => createMedicalTemplate(input, authorUid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: medicalTemplateKeys.all });
    },
  });
}

export function useUpdateMedicalTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      docId,
      data,
    }: {
      docId: string;
      data: UpdateMedicalTemplateInput;
    }) => updateMedicalTemplate(docId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: medicalTemplateKeys.all });
    },
  });
}

export function useDeleteMedicalTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (docId: string) => deleteMedicalTemplate(docId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: medicalTemplateKeys.all });
    },
  });
}
